import * as React from "react"

export type SelectionEffect = "none" | "latch" | "filter" | "row" | "chip"
export type SelectionCountFeedback = "none" | "pulse"
export type SelectionFeedbackPhase = "idle" | "settling"

export const SELECTION_SETTLE_DURATION_MS = 200
export const SELECTION_COUNT_PULSE_DURATION_MS = 220

const SELECTION_INTERACTION_STALE_WINDOW_MS = 1200

type Args = {
    selectionEffect: SelectionEffect
    selectionValue: string | number | null | undefined
    selectionCountFeedback?: SelectionCountFeedback
    visibleCount?: number | null
}

function normalizeSelectionValue(value: string | number | null | undefined) {
    if (value == null) {
        return null
    }

    const normalizedValue = String(value).trim()
    return normalizedValue.length > 0 ? normalizedValue : null
}

function normalizeVisibleCount(count?: number | null) {
    return typeof count === "number" && Number.isFinite(count) ? count : null
}

export function useSelectionInteractionFeedback({
    selectionEffect,
    selectionValue,
    selectionCountFeedback = "none",
    visibleCount,
}: Args) {
    const [feedbackPhase, setFeedbackPhase] = React.useState<SelectionFeedbackPhase>("idle")
    const [countChanged, setCountChanged] = React.useState(false)

    const mountedRef = React.useRef(false)
    const interactionArmedRef = React.useRef(false)
    const settleTimeoutRef = React.useRef<number | null>(null)
    const countTimeoutRef = React.useRef<number | null>(null)
    const staleInteractionTimeoutRef = React.useRef<number | null>(null)
    const previousSelectionValueRef = React.useRef<string | null>(normalizeSelectionValue(selectionValue))
    const previousVisibleCountRef = React.useRef<number | null>(normalizeVisibleCount(visibleCount))

    const clearTimeoutIfNeeded = React.useCallback((timeoutRef: React.MutableRefObject<number | null>) => {
        if (timeoutRef.current != null) {
            window.clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    const clearAllTimers = React.useCallback(() => {
        clearTimeoutIfNeeded(settleTimeoutRef)
        clearTimeoutIfNeeded(countTimeoutRef)
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)
    }, [clearTimeoutIfNeeded])

    React.useEffect(() => clearAllTimers, [clearAllTimers])

    const armSelectionInteraction = React.useCallback(() => {
        if (selectionEffect === "none" && selectionCountFeedback === "none") {
            return
        }

        interactionArmedRef.current = true
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)
        staleInteractionTimeoutRef.current = window.setTimeout(() => {
            interactionArmedRef.current = false
            staleInteractionTimeoutRef.current = null
        }, SELECTION_INTERACTION_STALE_WINDOW_MS)
    }, [clearTimeoutIfNeeded, selectionCountFeedback, selectionEffect])

    React.useEffect(() => {
        const nextSelectionValue = normalizeSelectionValue(selectionValue)
        const nextVisibleCount = normalizeVisibleCount(visibleCount)

        if (!mountedRef.current) {
            mountedRef.current = true
            previousSelectionValueRef.current = nextSelectionValue
            previousVisibleCountRef.current = nextVisibleCount
            return
        }

        const selectionChanged = previousSelectionValueRef.current !== nextSelectionValue
        const countChangedNow = previousVisibleCountRef.current !== nextVisibleCount

        previousSelectionValueRef.current = nextSelectionValue
        previousVisibleCountRef.current = nextVisibleCount

        if (!interactionArmedRef.current) {
            return
        }

        if (!selectionChanged && !countChangedNow) {
            return
        }

        interactionArmedRef.current = false
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)

        if (selectionEffect !== "none" && selectionChanged) {
            clearTimeoutIfNeeded(settleTimeoutRef)
            setFeedbackPhase("settling")
            settleTimeoutRef.current = window.setTimeout(() => {
                setFeedbackPhase("idle")
                settleTimeoutRef.current = null
            }, SELECTION_SETTLE_DURATION_MS)
        }

        if (selectionCountFeedback === "pulse" && countChangedNow && nextVisibleCount != null) {
            clearTimeoutIfNeeded(countTimeoutRef)
            setCountChanged(true)
            countTimeoutRef.current = window.setTimeout(() => {
                setCountChanged(false)
                countTimeoutRef.current = null
            }, SELECTION_COUNT_PULSE_DURATION_MS)
        }
    }, [clearTimeoutIfNeeded, selectionCountFeedback, selectionEffect, selectionValue, visibleCount])

    const getSelectionSurfaceProps = React.useCallback(({ active }: { active: boolean }) => ({
        "data-selection-surface": "true",
        "data-selection-effect": selectionEffect,
        "data-selection-active": active ? "true" : "false",
        "data-selection-phase": active ? feedbackPhase : "idle",
    } as const), [feedbackPhase, selectionEffect])

    const getSelectionCountProps = React.useCallback(() => ({
        "data-selection-count-feedback": selectionCountFeedback,
        "data-selection-count-changed": countChanged ? "true" : "false",
    } as const), [countChanged, selectionCountFeedback])

    return {
        feedbackPhase,
        countChanged,
        armSelectionInteraction,
        getSelectionSurfaceProps,
        getSelectionCountProps,
    }
}
